import React, { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import useFocusTrap from '../../hooks/useFocusTrap';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import './Modal.css';

/**
 * Accessible modal dialog.
 *
 * Handles what every hand-rolled overlay in the app used to redo (or skip):
 * Escape to close, click outside to close, a focus trap with focus restore,
 * `role="dialog"` / `aria-modal`, and a body scroll lock.
 *
 * @param {boolean} isOpen - Render the modal (default: true, for callers that mount conditionally)
 * @param {function} onClose - Called on Escape and on overlay click
 * @param {React.ReactNode} title - Rendered as the dialog heading and used as its accessible name
 * @param {string} ariaLabel - Accessible name when there is no visible title
 * @param {string} labelledBy - Id of an existing heading inside children to name the dialog
 * @param {boolean} closeOnOverlayClick - Close when the backdrop is clicked (default: true)
 * @param {boolean} closeOnEscape - Close on the Escape key (default: true)
 * @param {string} variant - 'panel' (Modal supplies backdrop + dialog chrome) or
 *   'bare' (Modal contributes behaviour only, the caller keeps its own CSS classes)
 * @param {string} size - 'sm' | 'md' | 'lg' | 'full'
 * @param {string} className - Extra classes on the dialog element
 * @param {string} overlayClassName - Extra classes on the backdrop element
 * @param {React.RefObject} initialFocusRef - Element focused when the modal opens
 */
function Modal({
    isOpen = true,
    onClose,
    title,
    ariaLabel,
    labelledBy,
    children,
    closeOnOverlayClick = true,
    closeOnEscape = true,
    variant = 'panel',
    size = 'md',
    className = '',
    overlayClassName = '',
    initialFocusRef,
    ...rest
}) {
    const dialogRef = useRef(null);
    const overlayRef = useRef(null);
    const generatedId = useId();
    const titleId = title ? `${generatedId}-title` : undefined;

    useFocusTrap(dialogRef, { active: isOpen, initialFocusRef });
    useBodyScrollLock(isOpen);

    useEffect(() => {
        if (!isOpen || !closeOnEscape || !onClose) return undefined;

        const handleEscape = (event) => {
            if (event.key === 'Escape') onClose(event);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, closeOnEscape, onClose]);

    // Compare against the overlay itself so a drag that ends on the backdrop
    // (text selection inside the dialog) does not close the modal.
    const handleOverlayClick = useCallback(
        (event) => {
            if (!closeOnOverlayClick || !onClose) return;
            if (event.target === overlayRef.current) onClose(event);
        },
        [closeOnOverlayClick, onClose]
    );

    if (!isOpen) return null;

    // 'bare' contributes no class of its own, so migrating an existing overlay
    // to Modal cannot change how it looks.
    const isBare = variant === 'bare';
    const overlayClasses = [!isBare && 'ui-modal-overlay', overlayClassName]
        .filter(Boolean)
        .join(' ');
    const dialogClasses = [!isBare && 'ui-modal', !isBare && `ui-modal--${size}`, className]
        .filter(Boolean)
        .join(' ');

    // Rendered through a portal so the overlay escapes any ancestor that would
    // trap it: a transformed/animated container (which turns position: fixed
    // into position: absolute) or a stacking context sitting below the sticky
    // sidebar and top bar.
    return createPortal(
        <div ref={overlayRef} className={overlayClasses} onClick={handleOverlayClick}>
            <div
                ref={dialogRef}
                className={dialogClasses}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId || labelledBy}
                aria-label={titleId || labelledBy ? undefined : ariaLabel}
                tabIndex={-1}
                {...rest}
            >
                {title && (
                    <h2 id={titleId} className="ui-modal__title">
                        {title}
                    </h2>
                )}
                {children}
            </div>
        </div>,
        document.body
    );
}

export default Modal;
